import { useState, useEffect, useCallback } from "react";
import { FiLink, FiPlus, FiSettings } from "react-icons/fi";
import { useTranslation } from "../context/TranslationContext";
import Badge from "../components/ds/Badge";
import Button from "../components/ds/Button";
import EmptyState from "../components/ds/EmptyState";
import Icon from "../components/ds/Icon";
import IconButton from "../components/ds/IconButton";
import TextField from "../components/ds/TextField";
import ConfirmModal from "../components/ConfirmModal";
import MintSpaceAppWindow from "../components/MintSpaceAppWindow";
import {
  getMintSpaceGateways,
  createMintSpaceGateway,
  updateMintSpaceGateway,
  deleteMintSpaceGateway,
} from "../api/mintSpaceGateway";

const FILLED = "!bg-app-gateway-base !text-app-gateway-on";

const hostnameFromUrl = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};

const MintSpaceGatewayPage = ({
  isDark,
  setToast,
  mintSpace,
  isAdmin = false,
  appInstanceId,
  onCreateProduct,
  onOpenProperties,
}) => {
  const { t } = useTranslation();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [label, setLabel] = useState("");
  const [url, setUrl] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [editUrl, setEditUrl] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);


  const mintSpaceId = mintSpace?._id;

  const loadLinks = useCallback(async () => {
    if (!mintSpaceId) return;
    try {
      setLoading(true);
      const res = await getMintSpaceGateways(mintSpaceId, appInstanceId);
      if (res.success) setLinks(res.data || []);
    } catch (err) {
      if (setToast) setToast(err.message || t("gateway.failedToLoad"));
    } finally {
      setLoading(false);
    }
  }, [mintSpaceId, appInstanceId, setToast, t]);

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!label.trim() || !url.trim()) return;
    setSaving(true);
    try {
      const res = await createMintSpaceGateway(mintSpaceId, {
        label: label.trim(),
        url: url.trim(),
        instanceId: appInstanceId,
      });
      if (res.success) {
        setLabel("");
        setUrl("");
        await loadLinks();
        if (setToast) setToast(t("gateway.created"));
      }
    } catch (err) {
      if (setToast) setToast(err.message || t("gateway.failedToSave"));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (item) => {
    setEditingId(item._id);
    setEditLabel(item.label);
    setEditUrl(item.url);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditLabel("");
    setEditUrl("");
  };

  const handleUpdate = async (linkId) => {
    if (!editLabel.trim() || !editUrl.trim()) return;
    setSaving(true);
    try {
      const res = await updateMintSpaceGateway(mintSpaceId, linkId, {
        label: editLabel.trim(),
        url: editUrl.trim(),
        instanceId: appInstanceId,
      });
      if (res.success) {
        cancelEdit();
        await loadLinks();
        if (setToast) setToast(t("gateway.updated"));
      }
    } catch (err) {
      if (setToast) setToast(err.message || t("gateway.failedToSave"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      const res = await deleteMintSpaceGateway(mintSpaceId, deleteTarget, appInstanceId);
      if (res.success) {
        await loadLinks();
        if (setToast) setToast(t("gateway.deleted"));
      }
    } catch (err) {
      if (setToast) setToast(err.message || t("gateway.failedToDelete"));
    } finally {
      setSaving(false);
      setDeleteTarget(null);
    }
  };

  const propertyMenuItems = [
    isAdmin &&
      onOpenProperties && {
        id: "properties",
        label: t("mintSpace.appProperties") || "Properties",
        icon: FiSettings,
        onClick: onOpenProperties,
      },
    isAdmin &&
      onCreateProduct && {
        id: "create-product",
        label: t("mintSpace.createProduct") || "Create product",
        icon: FiPlus,
        onClick: onCreateProduct,
      },
  ].filter(Boolean);

  return (
    <MintSpaceAppWindow
      isDark={isDark}
      app="gateway"
      icon={FiLink}
      iconGradient="from-violet-500 to-fuchsia-500"
      title={t("gateway.title")}
      subtitle={t("gateway.userSubtitle")}
      menuItems={propertyMenuItems}
    >
      <div className="flex flex-col gap-space-24">
        {loading ? (
          <p className="py-space-40 text-center text-subheadline text-label-secondary">{t("gateway.loading")}</p>
        ) : (
          <>
            {isAdmin && (
              <section className="flex flex-col gap-space-12 rounded-radius-lg bg-bg-raised p-space-16">
                <div className="flex flex-wrap items-center gap-space-8">
                  {[
                    t("gateway.stepPasteUrl"),
                    t("gateway.stepSetPrice"),
                    t("gateway.stepSellProduct"),
                    t("gateway.stepCheckout"),
                  ].map((step, index, steps) => (
                    <span key={step} className="inline-flex items-center gap-space-8">
                      <Badge>{step}</Badge>
                      {index < steps.length - 1 && <Icon name="chevron-right" size={16} className="text-label-tertiary" />}
                    </span>
                  ))}
                </div>
                <p className="text-footnote text-label-secondary">{t("gateway.howItWorksBody")}</p>
              </section>
            )}

            {isAdmin && (
              <form onSubmit={handleCreate} className="flex flex-col items-start gap-space-12 rounded-radius-lg bg-bg-raised p-space-16">
                <p className="text-caps-label uppercase text-app-gateway-soft">{t("gateway.addNew")}</p>
                <TextField
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder={t("gateway.labelPlaceholder")}
                  maxLength={120}
                />
                <TextField
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder={t("gateway.urlPlaceholder")}
                  icon={<Icon name="link" size={20} />}
                />
                <Button
                  type="submit"
                  size="m"
                  icon={<Icon name="plus" size={20} />}
                  className={saving || !label.trim() || !url.trim() ? "" : FILLED}
                  disabled={!label.trim() || !url.trim()}
                  loading={saving}
                >
                  {saving ? t("gateway.saving") : t("gateway.addLink")}
                </Button>
              </form>
            )}

            {links.length === 0 && (
              <EmptyState
                icon="link"
                title={t("gateway.emptyTitle")}
                message={isAdmin ? t("gateway.emptyAdmin") : t("gateway.emptyUser")}
              />
            )}

            {links.length > 0 && (
              <ul className="flex flex-col divide-y divide-separator-default rounded-radius-lg bg-bg-raised py-space-8">
                {links.map((item) =>
                  editingId === item._id && isAdmin ? (
                    <li key={item._id} className="flex flex-col gap-space-12 p-space-16">
                      <TextField value={editLabel} onChange={(e) => setEditLabel(e.target.value)} />
                      <TextField
                        type="url"
                        value={editUrl}
                        onChange={(e) => setEditUrl(e.target.value)}
                        icon={<Icon name="link" size={20} />}
                      />
                      <div className="flex gap-space-8">
                        <Button size="m" className={saving ? "" : FILLED} loading={saving} onClick={() => handleUpdate(item._id)}>
                          {t("common.save")}
                        </Button>
                        <Button size="m" variant="gray" onClick={cancelEdit}>
                          {t("common.cancel")}
                        </Button>
                      </div>
                    </li>
                  ) : (
                    <li key={item._id} className="flex items-center gap-space-12 px-space-16 py-space-12 min-[600px]:px-space-20">
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex min-w-0 flex-1 items-center gap-space-12"
                      >
                        <span className="flex size-9 shrink-0 items-center justify-center rounded-[12px] bg-app-gateway-deep text-app-gateway-soft">
                          <Icon name="link" size={18} />
                        </span>
                        <span className="flex min-w-0 flex-1 flex-col">
                          <span className="truncate text-subheadline-semibold text-label-primary">{item.label}</span>
                          <span className="truncate text-footnote text-label-secondary">{hostnameFromUrl(item.url)}</span>
                        </span>
                      </a>
                      {isAdmin && (
                        <>
                          <IconButton label={t("common.edit")} variant="plain" onClick={() => startEdit(item)}>
                            <Icon name="edit" size={18} />
                          </IconButton>
                          <IconButton label={t("common.delete")} variant="plain" className="text-status-danger" onClick={() => setDeleteTarget(item._id)}>
                            <Icon name="trash" size={18} />
                          </IconButton>
                        </>
                      )}
                    </li>
                  )
                )}
              </ul>
            )}
          </>
        )}
      </div>

      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => !saving && setDeleteTarget(null)}
        onConfirm={handleDelete}
        title={t("gateway.confirmDeleteTitle")}
        message={t("gateway.confirmDelete")}
        confirmLabel={t("common.delete")}
        isDark={isDark}
        variant="danger"
        loading={saving}
      />
    </MintSpaceAppWindow>
  );
};

export default MintSpaceGatewayPage;
